/**
 * HealthTab displays workflow execution health metrics: success/failure rates,
 * workflow types, publisher breakdown, and period-over-period trend comparison.
 */
import React, { useMemo } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Select, { SelectOption } from '@amzn/meridian/select';
import { DateTime } from 'luxon';
import { useTranslation } from 'src/arb-wrapper';
import { useQueryEntityMetricsQuery } from 'src/store/leela-api';
import { MetricCard } from 'src/components/entity-metrics/MetricCard';
import { StatusDonut } from 'src/components/entity-metrics/StatusDonut';
import {
  type TimePeriodValue,
  TIME_PERIOD_VALUES,
  TIME_PERIOD_KEYS,
  getDateRange,
} from 'src/components/entity-metrics/timePeriodUtils';

interface HealthTabProps {
  period: TimePeriodValue;
  onPeriodChange: (v: TimePeriodValue) => void;
}

export const HealthTab: React.FC<HealthTabProps> = ({ period, onPeriodChange }) => {
  const { t } = useTranslation();
  const dateRange = useMemo(() => getDateRange(period), [period]);
  const [startDate, endDate] = dateRange;

  // Calculate previous period (same duration, shifted back)
  const prevPeriod = useMemo(() => {
    const start = DateTime.fromISO(startDate);
    const end = DateTime.fromISO(endDate);
    const durationDays = Math.max(end.diff(start, 'days').days, 1);
    const prevEnd = start.toISODate() ?? startDate;
    const prevStart =
      start.minus({ days: durationDays }).toISODate() ?? startDate;
    return { startDate: prevStart, endDate: prevEnd };
  }, [startDate, endDate]);

  const workflowStatusQuery = useQueryEntityMetricsQuery({
    entity: 'workflows',
    groupBy: 'status',
    startDate,
    endDate,
  });

  const prevWorkflowStatusQuery = useQueryEntityMetricsQuery({
    entity: 'workflows',
    groupBy: 'status',
    startDate: prevPeriod.startDate,
    endDate: prevPeriod.endDate,
  });

  const workflowPublisherQuery = useQueryEntityMetricsQuery({
    entity: 'workflows',
    groupBy: 'publisher',
    startDate,
    endDate,
  });

  const workflowTypeQuery = useQueryEntityMetricsQuery({
    entity: 'workflows',
    groupBy: 'workflowType',
    startDate,
    endDate,
  });

  const workflowStatusData = useMemo(() => (workflowStatusQuery.data?.data ?? []).map((d) => ({ label: d.label ?? 'Unknown', value: d.value })), [workflowStatusQuery.data]);
  const workflowPublisherData = useMemo(() => (workflowPublisherQuery.data?.data ?? []).map((d) => ({ label: d.label ?? 'Unknown', value: d.value })), [workflowPublisherQuery.data]);
  const workflowTypeData = useMemo(() => (workflowTypeQuery.data?.data ?? []).map((d) => ({ label: d.label ?? 'Unknown', value: d.value })), [workflowTypeQuery.data]);

  const totalWorkflows = useMemo(() => workflowStatusData.reduce((sum, d) => sum + d.value, 0), [workflowStatusData]);
  const failedCount = useMemo(() => workflowStatusData.find((d) => d.label === 'FAILED')?.value ?? 0, [workflowStatusData]);
  const failureRate = useMemo(() => (totalWorkflows === 0 ? 0 : Math.round((failedCount / totalWorkflows) * 100)), [failedCount, totalWorkflows]);

  // Previous period metrics
  const prevWorkflowStatusData = useMemo(() => (prevWorkflowStatusQuery.data?.data ?? []).map((d) => ({ label: d.label ?? 'Unknown', value: d.value })), [prevWorkflowStatusQuery.data]);
  const prevTotalWorkflows = useMemo(() => prevWorkflowStatusData.reduce((sum, d) => sum + d.value, 0), [prevWorkflowStatusData]);
  const prevFailedCount = useMemo(() => prevWorkflowStatusData.find((d) => d.label === 'FAILED')?.value ?? 0, [prevWorkflowStatusData]);
  const prevFailureRate = useMemo(() => (prevTotalWorkflows === 0 ? 0 : Math.round((prevFailedCount / prevTotalWorkflows) * 100)), [prevFailedCount, prevTotalWorkflows]);

  const isError = workflowStatusQuery.isError;

  return (
    <Column spacing="400">
      <Row alignmentVertical="center" spacing="300">
        <Text type="b300">{t('label_time_period')}</Text>
        <Select value={period} width={200} onChange={onPeriodChange}>
          {TIME_PERIOD_VALUES.map((value) => (
            <SelectOption key={value} label={t(TIME_PERIOD_KEYS[value])} value={value} />
          ))}
        </Select>
      </Row>

      {isError && (
        <Row alignmentHorizontal="center">
          <Text color="error">{t('error_failed_to_load_workflow_data')}</Text>
        </Row>
      )}

      <Row spacing="400" widths="fill">
        <MetricCard isLoading={workflowStatusQuery.isLoading} label={t('metric_total_executions')} value={totalWorkflows} />
        <MetricCard isLoading={workflowStatusQuery.isLoading} label={t('metric_succeeded')} value={workflowStatusData.find((d) => d.label === 'COMPLETED')?.value ?? 0} />
        <MetricCard isLoading={workflowStatusQuery.isLoading} label={t('metric_failed')} value={failedCount} />
        <MetricCard invertTrend isLoading={workflowStatusQuery.isLoading} label={t('metric_failure_rate')} previousValue={prevFailureRate} units="%" value={failureRate} />
        <MetricCard isLoading={workflowStatusQuery.isLoading} label={t('metric_in_progress')} value={workflowStatusData.find((d) => d.label === 'IN_PROGRESS')?.value ?? 0} />
      </Row>

      <Row spacing="400" widths="fill">
        <StatusDonut data={workflowStatusData} isLoading={workflowStatusQuery.isLoading} title={t('chart_workflow_status')} />
        <StatusDonut data={workflowTypeData} isLoading={workflowTypeQuery.isLoading} title={t('chart_by_workflow_type')} />
      </Row>

      <StatusDonut data={workflowPublisherData} isLoading={workflowPublisherQuery.isLoading} title={t('chart_workflows_by_publisher')} />
    </Column>
  );
};
